'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'motion/react';
import React, { startTransition, useDeferredValue, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { UI_CONSTANTS } from '../types';
const MEDIA_CONFIGURATIONS = {
  video: {
    defaultWidth: 480,
    minWidth: 320,
    aspectRatio: 16 / 9,
    displayName: 'Video'
  },
  audio: {
    defaultWidth: 400,
    minWidth: 320,
    aspectRatio: 1,
    displayName: 'Audio'
  },
  stream: {
    defaultWidth: 400,
    minWidth: 320,
    aspectRatio: 16 / 9,
    displayName: 'Live Stream'
  },
  radio: {
    defaultWidth: 280,
    minWidth: 280,
    aspectRatio: 4 / 1,
    displayName: 'Live Radio'
  },
  podcast: {
    defaultWidth: 380,
    minWidth: 300,
    aspectRatio: 1,
    displayName: 'Podcast'
  },
  article: {
    defaultWidth: 320,
    minWidth: 280,
    aspectRatio: 1,
    displayName: 'Article Audio'
  }
};
const ANIMATION_VARIANTS = {
  visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    y: 0,
    transition: {
      type: 'tween',
      duration: 0.25,
      ease: 'easeIn'
    }
  },
  hidden: {
    opacity: 0,
    scale: 1,
    x: 0,
    y: 300
  }
};
const calculatePlayerDimensions = (mediaVariant, isMinimized, windowWidth, isMobile) => {
  const config = MEDIA_CONFIGURATIONS[mediaVariant] || MEDIA_CONFIGURATIONS.video;
  let width = isMinimized ? config.minWidth : Math.min(windowWidth - UI_CONSTANTS.CORNER_PADDING * 2, config.defaultWidth);
  let height = isMinimized ? 0 : Math.round(width / config.aspectRatio);
  if (isMobile) {
    width = isMinimized ? config.minWidth : Math.min(windowWidth - UI_CONSTANTS.CORNER_PADDING * 2, config.defaultWidth);
    height = isMinimized ? 0 : Math.round(width / config.aspectRatio);
  }
  return {
    width,
    height
  };
};
const getTransitionString = () => {
  return `all ${UI_CONSTANTS.ANIMATION_DURATION}s cubic-bezier(0.25, 0.1, 0.25, 1.0)`;
};
const getPlayerPositionStyles = (viewport, playerState, playerWidth) => {
  const {
    isMobile
  } = viewport;
  const {
    isHidden,
    position
  } = playerState;
  if (isMobile && isHidden) {
    return {
      top: 'auto',
      bottom: 0,
      left: 0,
      right: 0,
      width: '100%'
    };
  }
  if (isMobile) {
    return {
      top: 'auto',
      bottom: `${UI_CONSTANTS.CORNER_PADDING / 2}px`,
      left: 0,
      right: 0,
      width: '100%',
      margin: '0 auto',
      maxWidth: `${playerWidth}px`
    };
  }
  if (position === 'bottom-right') {
    return {
      top: 'auto',
      bottom: `${UI_CONSTANTS.CORNER_PADDING}px`,
      left: 'auto',
      right: `${UI_CONSTANTS.CORNER_PADDING}px`,
      width: `${playerWidth}px`
    };
  }
  return {
    top: 'auto',
    bottom: `${UI_CONSTANTS.CORNER_PADDING}px`,
    left: `${UI_CONSTANTS.CORNER_PADDING}px`,
    right: 'auto',
    width: `${playerWidth}px`
  };
};
const ChevronUpIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const ChevronDownIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const CornerLeftIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const CornerRightIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 10 20 15 15 20" /><path d="M4 4v7a4 4 0 0 0 4 4h12" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const MaximizeIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const MinimizeIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14h6v6" /><path d="M20 10h-6V4" /><path d="M14 10l7-7" /><path d="M3 21l7-7" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const CloseIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const ControlButton = t0 => {
  const $ = _c(10);
  const {
    onClick,
    disabled: t1,
    title,
    icon,
    className
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const t2 = disabled ? "default" : "pointer";
  const t3 = disabled ? 0.5 : 1;
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 4px",
      padding: "4px",
      cursor: t2,
      color: "white",
      border: "none",
      background: "none",
      touchAction: "manipulation",
      transition: "opacity 0.2s ease",
      opacity: t3
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== className || $[4] !== disabled || $[5] !== icon || $[6] !== onClick || $[7] !== t4 || $[8] !== title) {
    t5 = <button style={t4} onClick={onClick} disabled={disabled} title={title} className={className}>{icon}</button>;
    $[3] = className;
    $[4] = disabled;
    $[5] = icon;
    $[6] = onClick;
    $[7] = t4;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const PlayerHeader = t0 => {
  const $ = _c(33);
  const {
    title,
    viewport,
    playerState,
    mediaVariant,
    onToggleVisibility,
    onTogglePosition,
    onToggleMinimize,
    onClose
  } = t0;
  const {
    isMobile
  } = viewport;
  const {
    isHidden,
    isMinimized,
    isAnimating,
    position,
    hasError
  } = playerState;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 12px",
      height: `${UI_CONSTANTS.HEADER_HEIGHT}px`,
      color: "white",
      userSelect: "none"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const headerStyles = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "14px",
      fontWeight: 500,
      whiteSpace: "nowrap",
      margin: 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const titleStyles = t2;
  const isAudioVariant = mediaVariant === "radio" || mediaVariant === "article";
  const shouldShowMinimizeButton = (isMobile && !isHidden || !isMobile) && !isAudioVariant;
  const shouldShowPositionToggle = !isMobile;
  const shouldShowVisibilityToggle = isMobile;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      alignItems: "center",
      width: "100%"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== hasError) {
    t4 = hasError && <span style={{
      color: "#ff4444",
      fontSize: "12px",
      marginRight: "8px"
    }}>ERROR</span>;
    $[3] = hasError;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== title) {
    t5 = <h2 style={titleStyles}>{title}</h2>;
    $[5] = title;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      display: "flex"
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isHidden || $[9] !== onToggleVisibility || $[10] !== shouldShowVisibilityToggle) {
    t7 = shouldShowVisibilityToggle && <ControlButton onClick={onToggleVisibility} title="Toggle visibility" icon={isHidden ? <ChevronUpIcon /> : <ChevronDownIcon />} />;
    $[8] = isHidden;
    $[9] = onToggleVisibility;
    $[10] = shouldShowVisibilityToggle;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isAnimating || $[13] !== onTogglePosition || $[14] !== position || $[15] !== shouldShowPositionToggle) {
    t8 = shouldShowPositionToggle && <ControlButton onClick={onTogglePosition} disabled={isAnimating} title="Toggle corner position" icon={position === "bottom-left" ? <CornerLeftIcon /> : <CornerRightIcon />} />;
    $[12] = isAnimating;
    $[13] = onTogglePosition;
    $[14] = position;
    $[15] = shouldShowPositionToggle;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isMinimized || $[18] !== onToggleMinimize || $[19] !== shouldShowMinimizeButton) {
    t9 = shouldShowMinimizeButton && <ControlButton onClick={onToggleMinimize} title={isMinimized ? "Expand player" : "Minimize player"} icon={isMinimized ? <MaximizeIcon /> : <MinimizeIcon />} />;
    $[17] = isMinimized;
    $[18] = onToggleMinimize;
    $[19] = shouldShowMinimizeButton;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CloseIcon />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onClose) {
    t11 = <ControlButton onClick={onClose} title="Close player" icon={t10} />;
    $[22] = onClose;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t12 = <div style={t6}>{t7}{t8}{t9}{t11}</div>;
    $[24] = t11;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t4 || $[31] !== t5) {
    t13 = <header style={headerStyles}><div style={t3}>{t4}{t5}{t12}</div></header>;
    $[29] = t12;
    $[30] = t4;
    $[31] = t5;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};
const MediaPlayer = ({
  isVisible,
  title,
  mediaVariant = 'video',
  initialPosition = 'bottom-right',
  children,
  onClose
}) => {
  const [playerState, setPlayerState] = useState({
    isHidden: false,
    isMinimized: false,
    isAnimating: false,
    position: initialPosition
  });
  const [hasPlayerError] = useState(false);
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    isMobile: false
  });
  const containerRef = useRef(null);
  const animationTimerRef = useRef(null);
  const deferredViewport = useDeferredValue(viewport);
  const {
    width: playerWidth,
    height: playerHeight
  } = calculatePlayerDimensions(mediaVariant, playerState.isMinimized, deferredViewport.width, deferredViewport.isMobile);
  const containerHeight = useMemo(() => {
    if (deferredViewport.isMobile && playerState.isHidden) {
      return UI_CONSTANTS.BAR_HEIGHT;
    }
    return playerHeight + UI_CONSTANTS.HEADER_HEIGHT;
  }, [deferredViewport.isMobile, playerState.isHidden, playerHeight]);
  const handleTogglePosition = useCallback(() => {
    if (playerState.isAnimating) return;
    startTransition(() => {
      setPlayerState(prev => ({
        ...prev,
        isAnimating: true,
        position: prev.position === 'bottom-right' ? 'bottom-left' : 'bottom-right'
      }));
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      animationTimerRef.current = setTimeout(() => {
        setPlayerState(prev_0 => ({
          ...prev_0,
          isAnimating: false
        }));
        animationTimerRef.current = null;
      }, UI_CONSTANTS.ANIMATION_DURATION * 1000);
    });
  }, [playerState.isAnimating]);
  const handleToggleMinimize = useCallback(() => {
    startTransition(() => {
      setPlayerState(prev_1 => ({
        ...prev_1,
        isMinimized: !prev_1.isMinimized,
        isHidden: false
      }));
    });
  }, []);
  const handleToggleVisibility = useCallback(() => {
    startTransition(() => {
      setPlayerState(prev_2 => ({
        ...prev_2,
        isHidden: !prev_2.isHidden
      }));
    });
  }, []);
  useEffect(() => {
    const handleViewportChange = () => {
      const newWidth = window.innerWidth;
      const newIsMobile = newWidth < UI_CONSTANTS.MOBILE_BREAKPOINT;
      startTransition(() => {
        setViewport({
          width: newWidth,
          isMobile: newIsMobile
        });
      });
    };
    handleViewportChange();
    window.addEventListener('resize', handleViewportChange, {
      passive: true
    });
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);
  const containerStyles = useMemo(() => ({
    position: 'fixed',
    overflow: 'hidden',
    width: `${playerWidth}px`,
    minHeight: `${mediaVariant === 'article' ? 'auto' : containerHeight}px`,
    transition: getTransitionString(),
    borderRadius: deferredViewport.isMobile ? playerState.isHidden ? '0' : '6px' : '6px',
    backgroundColor: '#000c4d',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    zIndex: 9999,
    ...getPlayerPositionStyles(deferredViewport, {
      isHidden: playerState.isHidden,
      position: playerState.position
    }, playerWidth)
  }), [playerWidth, containerHeight, deferredViewport, playerState.isHidden, playerState.position]);
  return <AnimatePresence>
      {isVisible && <motion.div ref={containerRef} initial="hidden" animate="visible" exit="hidden" variants={ANIMATION_VARIANTS} style={containerStyles}>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%'
      }}>
            <PlayerHeader title={title} viewport={deferredViewport} playerState={{
          ...playerState,
          hasError: hasPlayerError
        }} mediaVariant={mediaVariant} onToggleVisibility={handleToggleVisibility} onTogglePosition={handleTogglePosition} onToggleMinimize={handleToggleMinimize} onClose={onClose} />
            <div style={{
          display: playerState.isHidden ? 'none' : 'block'
        }}>
              {children}
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
export default MediaPlayer;